import React, { useState, useEffect } from 'react';
import { Lock } from 'lucide-react';
import { useGetEtudiantsQuery } from '../../services/etudiantsApi';
import { useSaisieIndividuelleMutation } from '../../services/notesExamensApi';
import { typeFormationForClasse } from '../../utils/typeFormation';
import { useSemestreCloture } from '../../hooks/useSemestreCloture';
import { useGetAnneeActiveQuery } from '../../services/anneeAcademiqueApi';

interface SaisieIndividuelleExamensProps {
  matiereId: number;
  anneeAcademique: string;
  semestre: 'S1' | 'S2';
  classe: string;
  estRattrapage: boolean;
  onSuccess?: () => void;
}

const SaisieIndividuelleExamens: React.FC<SaisieIndividuelleExamensProps> = ({
  matiereId,
  anneeAcademique,
  semestre,
  classe,
  estRattrapage,
  onSuccess,
}) => {
  // CS si la classe finit par "-CS", sinon FI + DD.
  const { data: etudiants, isLoading: loadingEtudiants } = useGetEtudiantsQuery({ classe, type_formation: typeFormationForClasse(classe) });
  const [saisieIndividuelle, { isLoading: isSaving }] = useSaisieIndividuelleMutation();

  const [etudiantId, setEtudiantId] = useState<number | null>(null);
  const [noteExamen, setNoteExamen] = useState<string>('');
  const [absent, setAbsent] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Fix 2026-08-28 : détection semestre clôturé.
  // Fix 2026-10-15 : les verrous ne s'appliquent qu'à l'année active.
  const { data: anneeActive } = useGetAnneeActiveQuery();
  const estAnneeActive = !anneeActive || anneeAcademique === anneeActive.libelle;
  const semestreCloture = useSemestreCloture(semestre, estRattrapage, estAnneeActive);

  // Réinitialiser le formulaire
  const resetForm = () => {
    setEtudiantId(null);
    setNoteExamen('');
    setAbsent(false);
  };

  // Gérer le changement d'absence
  useEffect(() => {
    if (absent) {
      setNoteExamen('0');
    }
  }, [absent]);

  // Soumettre le formulaire
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    // Fix 2026-08-28 : blocage si semestre clôturé
    if (semestreCloture) {
      setMessage({ type: 'error', text: `🔒 ${semestreCloture.label} — enregistrement refusé` });
      return;
    }

    if (!etudiantId) {
      setMessage({ type: 'error', text: 'Veuillez sélectionner un étudiant' });
      return;
    }

    const noteValue = noteExamen === '' ? null : parseFloat(noteExamen);
    if (noteValue !== null && (noteValue < 0 || noteValue > 20)) {
      setMessage({ type: 'error', text: 'La note doit être comprise entre 0 et 20' });
      return;
    }

    try {
      const result = await saisieIndividuelle({
        etudiant_id: etudiantId,
        matiere_id: matiereId,
        annee_academique: anneeAcademique,
        semestre: semestre,
        note_examen: noteValue,
        absent: absent,
        est_rattrapage: estRattrapage,
      }).unwrap();

      if (result.success) {
        setMessage({
          type: 'success',
          text: result.created ? 'Note créée avec succès !' : 'Note mise à jour avec succès !',
        });
        resetForm();
        onSuccess?.();
      } else {
        setMessage({ type: 'error', text: 'Erreur lors de l\'enregistrement' });
      }
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.data?.error || error.data?.message || 'Erreur lors de l\'enregistrement de la note',
      });
    }
  };

  if (loadingEtudiants) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Chargement des étudiants...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-6">
        Saisie Individuelle - Notes d'Examens
        {estRattrapage && <span className="ml-2 text-orange-600 dark:text-orange-400 text-sm">(Session Rattrapage)</span>}
      </h2>

      {/* Bandeau SEMESTRE CLÔTURÉ (fix 2026-08-28) */}
      {semestreCloture && (
        <div className="mb-4 rounded-lg border-2 border-red-400 dark:border-red-700 bg-red-50 dark:bg-red-900/20 px-4 py-3 flex items-center gap-3">
          <Lock size={22} className="text-red-600 dark:text-red-400 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-bold text-red-800 dark:text-red-200 text-sm uppercase tracking-wide">
              🔒 Saisie verrouillée — {semestreCloture.label}
            </div>
            <div className="text-xs text-red-700 dark:text-red-300 mt-0.5">
              Vos modifications seront <strong>refusées</strong> à l'enregistrement.
              Contactez le super-administrateur pour rouvrir la saisie.
            </div>
          </div>
        </div>
      )}

      {/* Message */}
      {message && (
        <div className={`mb-6 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-700' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-700'}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Sélection de l'étudiant */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Étudiant <span className="text-red-500">*</span>
          </label>
          <select
            value={etudiantId || ''}
            onChange={(e) => setEtudiantId(e.target.value ? parseInt(e.target.value) : null)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
          >
            <option value="">-- Sélectionner un étudiant --</option>
            {etudiants?.map((etudiant) => (
              <option key={etudiant.id} value={etudiant.id}>
                {etudiant.matricule} - {etudiant.nom} {etudiant.prenoms}
              </option>
            ))}
          </select>
        </div>

        {/* Saisie de la note */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Note Examen (0-20)
            </label>
            <input
              type="number"
              step="0.25"
              min="0"
              max="20"
              value={noteExamen}
              onChange={(e) => setNoteExamen(e.target.value)}
              disabled={absent}
              placeholder="Ex: 15.5"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-600 dark:bg-gray-700 dark:text-gray-100"
            />
          </div>

          <div className="flex items-end">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={absent}
                onChange={(e) => setAbsent(e.target.checked)}
                className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Absent à l'examen</span>
            </label>
          </div>
        </div>

        {/* Prévisualisation de la note */}
        {etudiantId && (noteExamen || absent) && (
          <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Récapitulatif</h3>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <p>
                <strong>Étudiant:</strong>{' '}
                {etudiants?.find((e) => e.id === etudiantId)?.nom}{' '}
                {etudiants?.find((e) => e.id === etudiantId)?.prenoms}
              </p>
              <p>
                <strong>Note Examen:</strong>{' '}
                {absent ? (
                  <span className="text-yellow-600 dark:text-yellow-400">0 (Absent)</span>
                ) : noteExamen ? (
                  <span className={parseFloat(noteExamen) < 5 ? 'text-red-600 dark:text-red-400' : parseFloat(noteExamen) >= 10 ? 'text-green-600 dark:text-green-400' : 'text-blue-600 dark:text-blue-400'}>
                    {noteExamen}/20
                    {parseFloat(noteExamen) < 5 && ' (Éliminatoire)'}
                    {parseFloat(noteExamen) >= 10 && ' (Validé)'}
                  </span>
                ) : (
                  'Non saisie'
                )}
              </p>
            </div>
          </div>
        )}

        {/* Boutons */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={resetForm}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Réinitialiser
          </button>
          <button
            type="submit"
            disabled={!etudiantId || isSaving || !!semestreCloture}
            title={semestreCloture ? `🔒 ${semestreCloture.label} — enregistrement refusé` : ''}
            className="px-4 py-2 bg-blue-600 text-white rounded-md shadow-sm text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Enregistrement...' : 'Enregistrer la note'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SaisieIndividuelleExamens;
